import React from "react";
import { ReactComponent as BoxIcon } from "../../../assets/svg/box.svg";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PlagiarismOutlinedIcon from "@mui/icons-material/PlagiarismOutlined";
import { IconPage, IconVideo } from "../../../utils/icons";
import { BrandProject } from "./constants";
import StatusIcon from "./StatusIcon";
import DownloadStatusIcon from "./DownloadStatusIcon";
import {
    getBriefIconStatus,
    getExpertIconStatus,
    getTdcShippingIconStatus,
    getExpertShippingIconStatus,
    getReviewIconStatus,
    getPhaseColor,
    getExpertCounts,
    getVideoCounts,
    formatExpertCountSummary,
    formatVideoCountSummary,
    getProductTestingIconStatus,
    getProductTestingExpertCount,
    showProductTestingIcon,
} from "../helper";

interface ProjectStatusIconsProps {
    project: BrandProject;
    role: string;
    user?: any;
    onBriefClick: () => void;
    onExpertClick: () => void;
    onShippingClick: () => void;
    onReviewClick: () => void;
    onProductTestingClick: () => void;
}

const ProjectStatusIcons: React.FC<ProjectStatusIconsProps> = ({
    project,
    role,
    user,
    onBriefClick,
    onExpertClick,
    onShippingClick,
    onReviewClick,
    onProductTestingClick,
}) => {
    const briefStatus = getBriefIconStatus(project, role, user);
    const expertStatus = getExpertIconStatus(project, role, user);
    const tdcShippingStatus = getTdcShippingIconStatus(project, role, user);
    const expertShippingStatus = getExpertShippingIconStatus(project, role, user);
    const reviewStatus = getReviewIconStatus(project, role, user);
    const productTestingStatus = getProductTestingIconStatus(project);

    const expertCounts = getExpertCounts(project);
    const videoCounts = getVideoCounts(project);

    const expertTooltip =
        expertCounts.proposed > 0
            ? `${expertStatus.tooltip} ( ${formatExpertCountSummary(expertCounts)} )`
            : expertStatus.tooltip;
    const reviewTooltip =
        videoCounts.total > 0
            ? `${reviewStatus.tooltip} ( ${formatVideoCountSummary(videoCounts)} )`
            : reviewStatus.tooltip;

    return (
        <div style={{ display: "flex", alignItems: "center", gap: 0, flexShrink: 0 }}>
            <StatusIcon
                phase={briefStatus.phase}
                needsAction={briefStatus.needsAction}
                tooltip={briefStatus.tooltip}
                icon={<IconPage size={20} stroke={getPhaseColor(briefStatus.phase)} />}
                onClick={onBriefClick}
            />
            <StatusIcon
                phase={expertStatus.phase}
                needsAction={expertStatus.needsAction}
                tooltip={expertTooltip}
                count={expertCounts.proposed}
                icon={<PersonOutlineIcon sx={{ fontSize: 20 }} />}
                onClick={onExpertClick}
            />
            <StatusIcon
                phase={tdcShippingStatus.phase}
                needsAction={tdcShippingStatus.needsAction}
                tooltip={tdcShippingStatus.tooltip}
                icon={<BoxIcon style={{ width: 20, height: 20 }} />}
                label="TDC"
                onClick={onShippingClick}
            />
            <StatusIcon
                phase={expertShippingStatus.phase}
                needsAction={expertShippingStatus.needsAction}
                tooltip={expertShippingStatus.tooltip}
                icon={<BoxIcon style={{ width: 20, height: 20 }} />}
                label="EXPERT"
                onClick={onShippingClick}
            />
            <StatusIcon
                phase={reviewStatus.phase}
                needsAction={reviewStatus.needsAction}
                tooltip={reviewTooltip}
                count={videoCounts.total}
                icon={<IconVideo size={20} stroke={getPhaseColor(reviewStatus.phase)} />}
                onClick={onReviewClick}
            />
            {showProductTestingIcon(project, role) && (
                <StatusIcon
                    phase={productTestingStatus.phase}
                    needsAction={productTestingStatus.needsAction}
                    tooltip={productTestingStatus.tooltip}
                    count={getProductTestingExpertCount(project)}
                    icon={<PlagiarismOutlinedIcon sx={{ fontSize: 20 }} />}
                    onClick={onProductTestingClick}
                />
            )}
            <DownloadStatusIcon project={project} role={role} user={user} />
        </div>
    );
};

export default ProjectStatusIcons;
